import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { enumerators } from 'src/dictionaries/enum.pt-br';
import { SignalingTypeEnum, SystemModuleEnum } from 'src/enumerators';
import { environment } from 'src/environments/environment';
import { Log, MemberNotificationSetting } from 'src/models';
import { AuthService } from './auth.service';
import { ContractService } from './contract.service';
import { LogsService } from './logs.service';
import { RestApiService } from './rest-api.service';
import { ToastService } from './toast.service';


type NotificationEntry = [Log, string];
type NotificationDayGroup = [string, NotificationEntry[]];

interface WorkspaceMemberRef {
  id?: string;
  userId?: string;
  user?: any;
}

interface WorkspaceMembersResponse {
  members?: WorkspaceMemberRef[];
}

@Injectable({
  providedIn: 'root'
})
export class SocketService extends RestApiService {
  private _socketConnection: Socket | null = null;
  private readonly _urlNotifications = '/api/notification';
  private readonly LAST_LOG = 'LAST_LOG_DATE';
  private readonly femaleModules = new Set<SystemModuleEnum>([
    SystemModuleEnum.SignalingRegister,
    SystemModuleEnum.Area,
    SystemModuleEnum.Team,
    SystemModuleEnum.Task,
    SystemModuleEnum.ServiceOrder,
    SystemModuleEnum.Routes,
  ]);
  private readonly notificationDateFormat: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  };

  eventNotifications: BehaviorSubject<Log> = new BehaviorSubject(undefined as unknown as Log);
  eventNotificationsVehicle: BehaviorSubject<Log> = new BehaviorSubject(undefined as unknown as Log);
  eventReportNotification: BehaviorSubject<any> = new BehaviorSubject(undefined);
  listNotifications: Array<Log> = [];
  listGroupNotifications: Array<Log> = [];
  listNotificationsDays: Array<any> = [];
  listGroupNotificationsDays: Array<any> = [];
  listUsers: any[] = [];
  showNewLog = false;
  lastLogDate: Date | undefined;
  memberNotificationSettings: MemberNotificationSetting = new MemberNotificationSetting({});

  constructor(
    public http: HttpClient,
    private _auth: AuthService,
    private _contract: ContractService,
    private _logsService: LogsService,
    private _toastService: ToastService,
  ) {
    super(http);
  }

  // Iniciar Conexão Socket com o Servidor
  StartNotificationsSocket(): void {
    if (this.hasActiveSocketConnection()) return;

    this.loadInitialNotifications();
    this.createSocketConnection();
  }

  // GET das configurações de notificações do membro
  getMemberNotificationSettings(): void {
    this._logsService.getMemberNotificationsSetting().subscribe((res) => {
      if (!Array.isArray(res)) {
        this.memberNotificationSettings = res;
      }
    });
  }

  // Encerrar Conexão Socket com o Servidor
  StopNotificationsSocket(): void {
    if (this._socketConnection?.connected) {
      this._socketConnection.disconnect();
    }
  }

  // Salva a data do ultimo log no localstorage
  storeLastLog(lastLog?: Date): void {
    if (!this.isValidDate(lastLog)) return;
    localStorage.setItem(this.LAST_LOG, lastLog.toISOString());
  }

  // Retorna a data do ultimo log do localstorage
  getLastLog(): Date | null {
    const storedLastLog = localStorage.getItem(this.LAST_LOG);

    if (!storedLastLog) return null;

    const lastLog = new Date(storedLastLog);
    return this.isValidDate(lastLog) ? lastLog : null;
  }

  createLogsDays(listDays: Array<Log>): Array<any> {
    const list = this.GroupDates(listDays);

    if (this.shouldHighlightNewLogs()) {
      this.showNewLog = true;
      this.storeLastLog(this.lastLogDate);
    }

    return list;
  }

  usersLogs(listLogs: Array<Log>): Array<Log> {
    return listLogs.map((log: Log) => this.attachUserToLog(log));
  }

  // Escutar Notificações
  ListenNotifications(): void {
    if (!this._socketConnection?.connected) return;

    this.bindSocketEvent('notification', (res: any) => this.handleNotificationEvent(res));
    this.bindSocketEvent('team-location', (res: any) => this.eventNotificationsVehicle.next(res));
    this.bindSocketEvent('check-in', (res: any) => this.eventNotificationsVehicle.next(res));
    this.bindSocketEvent('task-status-update', (res: any) => this.eventNotificationsVehicle.next(res));
    this.bindSocketEvent('check-out', (res: any) => this.emitCheckoutNotification(res));
    this.bindSocketEvent('team-check-out', (res: any) => this.emitCheckoutNotification(res));
    this.bindSocketEvent('report', (res: any) => this.eventReportNotification.next(res));
  }

  // Agrupar Logs por Data
  GroupDates(logs: Array<Log>, fullBool: boolean = false): Array<any> {
    const datesRef: Record<string, NotificationEntry[]> = {};

    logs.forEach((log: Log) => {
      const date = this.formatLogDate(log.createdAt);
      const text = fullBool ? this.getNotificationDescriptionFull(log) : this.getNotificationDescription(log);

      if (datesRef[date]) {
        datesRef[date].push([log, text]);
      } else {
        datesRef[date] = [[log, text]];
      }
    });

    return this.replaceRelativeDateLabels(Object.entries(datesRef) as NotificationDayGroup[]);
  }

  isFemaleGender(log: Log): boolean {
    return this.femaleModules.has(log.module);
  }

  // Cria o texto para cada log
  getNotificationDescription(log: Log): string {
    if (log.module == SystemModuleEnum.Warranty) {
      return this.getWarrantyAlertMessage(log);
    }

    const genderOrGroupText = log.isGroup ? log.data.quantity : this.getGenderText(log);
    const auxGroup = log.isGroup ? 'GROUP' : 'SINGLE';

    return `${this.getNotificationActorPrefix(log)}${this.getActionSimpleText(log.action)} ${genderOrGroupText} ${this.getSystemModuleText(log.module, auxGroup)}`;
  }

  // Cria o texto para cada log em historico
  getNotificationDescriptionFull(log: Log): string {
    if (log.module == SystemModuleEnum.Warranty) {
      return this.getWarrantyAlertMessage(log);
    }

    if (log.module == SystemModuleEnum.Team) {
      return this.getTeamNotificationDescription(log);
    }

    const prefix = `${this.getNotificationActorPrefix(log)}${this.getActionSimpleText(log.action)} ${this.getGenderText(log)} `;

    switch (log.module) {
      case SystemModuleEnum.Contract:
      case SystemModuleEnum.Project:
        return `${prefix} ${this.getSystemModuleText(log.module, 'SINGLE')} "${log.data.name}".`;

      case SystemModuleEnum.SignalingRegister:
        return `${prefix}${this.getSignalingNotificationTarget(log)}`;

      case SystemModuleEnum.Region:
      case SystemModuleEnum.Area:
        return `${prefix}${this.getSystemModuleText(log.module, 'SINGLE')}`;

      case SystemModuleEnum.Vehicle:
        return `${prefix}"${this.getDictionaryValue(enumerators.VehicleType, log.data.vehicleType)}".`;

      case SystemModuleEnum.Task:
        return `${prefix}tarefa de ${this.getDictionaryValue(enumerators.SignalingType, log.data.signalingType)} '${log.data.name}' com ${this.getDictionaryValue(enumerators.OrderPriority, log.data.priority)}.`;

      case SystemModuleEnum.ServiceOrder:
        return `${prefix}ordem de serviço '${log.data.name}'.`;

      case SystemModuleEnum.Path:
        return `${prefix}trajeto '${log.data.name}' com ${this.getFormattedLength(log.data.length)}km de extensão.`;

      case SystemModuleEnum.Routes:
        return `${prefix}rota '${log.data.name}' com ${this.getFormattedLength(log.data.length)}km de extensão.`;

      case SystemModuleEnum.Stock:
        return `${prefix}${this.getStockNotificationTarget(log)}`;

      default:
        return prefix;
    }
  }

  private hasActiveSocketConnection(): boolean {
    return !!this._socketConnection && !this._socketConnection.disconnected;
  }

  private loadInitialNotifications(): void {
    this.getMemberNotificationSettings();

    this._contract.listAllWorkspaceUsers({ files: false }).pipe(
      map((res: WorkspaceMembersResponse) => res?.members ?? []),
      tap((members: WorkspaceMemberRef[]) => {
        this.listUsers = members;
      }),
      switchMap(() => forkJoin({
        notifications: this.getLogsList(0, 10),
        groupNotifications: this.getGroupLogsList(30, this.getGroupLogsStartDate()),
      })),
    ).subscribe(({ notifications, groupNotifications }) => {
      this.applyFetchedLogs(notifications, false);
      this.applyFetchedLogs(groupNotifications, true);
    });
  }

  private createSocketConnection(): void {
    const url = environment.endpoint();
    const token = this._auth.getJwtToken();

    if (!token) return;

    this._socketConnection = io(url, {
      path: this._urlNotifications,
      transports: ['polling', 'websocket'],
      auth: { token },
    });

    this.bindSocketEvent('connect', () => {
      console.log('Conexão Estabelecida (Notificações)!');
      this.ListenNotifications();
    });
    this.bindSocketEvent('connect_error', (err: Error) => {
      console.error('Erro na conexão Socket.io:', err.message, err);
    });
  }

  private bindSocketEvent(eventName: string, handler: (...args: any[]) => void): void {
    if (!this._socketConnection) return;

    this._socketConnection.off(eventName);
    this._socketConnection.on(eventName, handler);
  }

  private applyFetchedLogs(logs: Array<Log>, isGroupNotification: boolean): void {
    const normalizedLogs = this.usersLogs(logs || []);

    this.lastLogDate = normalizedLogs[0]?.updatedAt;
    if (!this.getLastLog()) {
      this.storeLastLog(this.lastLogDate);
    }

    if (isGroupNotification) {
      this.listGroupNotifications = normalizedLogs;
      this.listGroupNotificationsDays = this.createLogsDays(normalizedLogs);
      return;
    }

    this.listNotifications = normalizedLogs;
    this.listNotificationsDays = this.createLogsDays(normalizedLogs);
  }

  private handleNotificationEvent(res: any): void {
    const log = this.attachUserToLog(new Log(res));

    this.eventNotifications.next(log);
    this.listNotifications = [log, ...this.listNotifications];
    this.listGroupNotifications = [log, ...this.listGroupNotifications];
    this.listNotificationsDays = this.GroupDates(this.listNotifications);
    this.listGroupNotificationsDays = this.GroupDates(this.listGroupNotifications);
    this.showNewLog = true;
    this.lastLogDate = log.updatedAt;
    this.storeLastLog(log.updatedAt);

    if (this.memberNotificationSettings.viewToastNotification) {
      this._toastService.showNotification(this.getNotificationDescriptionFull(log));
    }
  }

  private emitCheckoutNotification(res: any): void {
    res.checkout = true;
    this.eventNotificationsVehicle.next(res);
  }

  private attachUserToLog(log: Log): Log {
    const user = this.listUsers?.find((member: WorkspaceMemberRef) => member.userId == log.userId);

    if (user) {
      log.user = user.user;
    }

    return log;
  }

  private shouldHighlightNewLogs(): boolean {
    const storedLastLog = this.getLastLog();

    return this.isValidDate(this.lastLogDate) && (!storedLastLog || this.lastLogDate > storedLastLog);
  }

  private getGroupLogsStartDate(): Date {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 5);
    startDate.setHours(0, 0, 0, 0);
    return startDate;
  }

  private getLogsList(offset: number, limit: number): Observable<Log[]> {
    return this._logsService.getLogs(offset, limit).pipe(
      map((res) => this.ensureLogArray(res)),
    );
  }

  private getGroupLogsList(length: number, startDate: Date): Observable<Log[]> {
    return this._logsService.getLogsGroup(length, startDate).pipe(
      map((res) => this.ensureLogArray(res)),
    );
  }

  private ensureLogArray(res: Log | Log[]): Log[] {
    return Array.isArray(res) ? res : [res];
  }

  private replaceRelativeDateLabels(list: NotificationDayGroup[]): NotificationDayGroup[] {
    const today = this.formatLogDate(new Date());

    if (list[0]?.[0] == today) {
      list[0][0] = 'Hoje';
    }

    const yesterdayDate = new Date();
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterday = this.formatLogDate(yesterdayDate);

    if (list[0]?.[0] == yesterday) {
      list[0][0] = 'Ontem';
    } else if (list[1]?.[0] == yesterday) {
      list[1][0] = 'Ontem';
    }

    return list;
  }

  private formatLogDate(date: Date): string {
    return new Date(date).toLocaleDateString('pt-br', this.notificationDateFormat);
  }

  private isValidDate(date?: Date | null): date is Date {
    return date instanceof Date && !Number.isNaN(date.getTime());
  }

  private getGenderText(log: Log): string {
    return this.isFemaleGender(log) ? 'uma' : 'um';
  }

  private getNotificationActorPrefix(log: Log): string {
    const firstName = log?.user?.firstName || '';
    const lastName = log?.user?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName ? `${fullName} ` : '';
  }

  private getActionSimpleText(action: unknown): string {
    return this.getDictionaryValue(enumerators.ActionSimpleText, action);
  }

  private getSystemModuleText(module: unknown, scope: 'GROUP' | 'SINGLE'): string {
    const dictionary = enumerators.SystemModuleText as Record<string, { GROUP?: string; SINGLE?: string }>;
    const moduleText = module == null ? undefined : dictionary[String(module)];

    return moduleText?.[scope] || '';
  }

  private getDictionaryValue(dictionary: Record<string, string>, key: unknown): string {
    if (key == null) return '';

    return dictionary[String(key)] || '';
  }

  private getWarrantyAlertCount(log: Log): number {
    const total = ['device', 'horizontal', 'vertical']
      .reduce((sum, key) => {
        const value = Number(log?.data?.[key]);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0);

    if (total > 0) return total;

    const fallbackValue = Number(log?.data?.quantity ?? log?.data?.signalingQuantity ?? log?.data?.total);
    return Number.isFinite(fallbackValue) ? fallbackValue : 0;
  }

  private getWarrantyAlertMessage(log: Log): string {
    const numberSigns = this.getWarrantyAlertCount(log);
    const sign = numberSigns == 1 ? 'sinalização entrou' : 'sinalizações entraram';

    return `${numberSigns} ${sign} em alerta de fim da garantia.`;
  }

  private getTeamNotificationDescription(log: Log): string {
    const leader = this.listUsers?.find((user: WorkspaceMemberRef) => user.id == log.data.leaderMemberId);
    return `Equipe de ${log.data.type} '${log.data.name}' foi criada com o líder '${leader?.user?.firstName} ${leader?.user?.lastName}'`;
  }

  private getSignalingNotificationTarget(log: Log): string {
    const signCode = log?.data?.verticalCode
      || log?.data?.horizontalCode
      || log?.data?.deviceCode
      || log?.data?.trafficSigns?.[0]?.verticalCode;
    const verticalType = log?.data?.trafficSigns?.length
      ? log?.data?.trafficSigns?.[0]?.verticalType
      : log?.data?.verticalType || '';
    const signName = this.getDictionaryValue(enumerators.Signaling, signCode);
    const signType = verticalType ? this.getDictionaryValue(enumerators.SignalizationType, verticalType) : '';

    return `sinalização de ${signType} "${signName}".`;
  }

  private getStockNotificationTarget(log: Log): string {
    const code = log.data.type == SignalingTypeEnum.Device
      ? log.data.deviceCode
      : log.data.type == SignalingTypeEnum.Vertical
        ? log.data.verticalCode
        : log.data.horizontalCode;
    const signName = this.getDictionaryValue(enumerators.Signaling, code);
    const signType = this.getDictionaryValue(enumerators.SignalingType, log.data.type);

    return `estoque de ${signType} "${signName}".`;
  }

  private getFormattedLength(length: number): string {
    const numericLength = Number(length);
    return Number.isFinite(numericLength) ? numericLength.toFixed(2) : '0.00';
  }
}
